"use client"

import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"

import { cn } from "@/lib/utils"
import { usePopupLayer } from "@/lib/layers/LayerOwner"
import { OPTION_TONES, overlay, type OptionTone, type OverlaySize } from "./overlay-recipes"

/**
 * The list (content, items, labels, separators, scroll buttons) is the ONE dropdown look shared with the menu, the
 * popover and the native-select adapter (components/ui/overlay-recipes.ts) for EVERY caller (std-overlays G01,
 * docs/THEME.md 2.10). `look` now only picks the trigger (2.16):
 * - "legacy" (the default): the trigger keeps the classes and 40px height it always had (field layout is per-family
 *   migration work, not a source re-skin).
 * - "standard" (`<Select look="standard">`): the standard trigger, sized by `size`.
 * The list portals into its OWNER's layer slot when it is inside a migrated modal or the page layer root
 * (lib/layers/LayerOwner.tsx); with no owner it keeps today's body portal and z.
 */
export type SelectLook = "legacy" | "standard"
const SelectLookContext = React.createContext<SelectLook>("legacy")
const useLook = () => React.useContext(SelectLookContext)

function Select({ look = "legacy", ...props }: React.ComponentProps<typeof SelectPrimitive.Root> & { look?: SelectLook }) {
  return (
    <SelectLookContext.Provider value={look}>
      <SelectPrimitive.Root {...props} />
    </SelectLookContext.Provider>
  )
}

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & { size?: OverlaySize }
>(({ className, children, size = "md", ...props }, ref) => {
  const look = useLook()
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        look === "standard"
          ? cn(overlay.trigger, overlay.triggerSize[size])
          : "flex h-10 w-full items-center justify-between rounded-md border border-edge bg-raised text-ink px-3 py-2 text-sm ring-offset-background placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        {look === "standard" ? <ChevronDown className={overlay.triggerIcon} aria-hidden="true" /> : <ChevronDown className="h-4 w-4 opacity-50" />}
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
})
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronUp className="icon" aria-hidden="true" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronDown className="icon" aria-hidden="true" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

/** The Select list's layer with no owner (see SelectContent). Exported so an enriched popup that sits beside Select
 *  lists (UserSearchPicker) takes the same layer instead of inventing one. */
export const SELECT_LIST_LEGACY_Z = "z-[100102]"

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & { container?: HTMLElement | null }
>(({ className, children, position = "popper", container, onCloseAutoFocus, ...props }, ref) => {
  // z-[100102] (no owner): ABOVE every dialog layer (Dialog overlay 10000 / content 10001, DialogContentNested 100100 /
  // 100101). Portalled to body, so z-50 put a Select opened inside a Dialog BEHIND the dialog's overlay (UI review
  // 2026-10-23, call-setup B07). With an owner the list renders in the owner's slot instead (2.10).
  const layer = usePopupLayer(SELECT_LIST_LEGACY_Z)
  return (
  <SelectPrimitive.Portal container={container ?? layer.container}>
    <SelectPrimitive.Content
      ref={ref}
      data-popup-owned={layer.owned ? "" : undefined}
      onCloseAutoFocus={(event) => {
        onCloseAutoFocus?.(event)
        if (document.activeElement?.tagName === "IFRAME") event.preventDefault()
      }}
      className={cn(
        // The surface's p-1 sits on the viewport (below) so the scroll buttons stay flush with the edge.
        "relative max-h-[min(24rem,var(--radix-select-content-available-height))] min-w-[8rem] overflow-hidden",
        overlay.popupSurface,
        "p-0",
        overlay.popupMaxWidth,
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        layer.zClass,
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
  )
})
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label ref={ref} className={cn(overlay.groupLabel, className)} {...props} />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    /** A leading icon, shown in the list and in the trigger. */
    icon?: React.ReactNode
    /** The icon's colour (a token tone). */
    iconTone?: OptionTone
    /** Put the icon on a tinted square (in its tone, or the raised fill). */
    iconTile?: boolean
    /** A colour swatch before the label, shown in the list and in the trigger. */
    swatch?: OptionTone
    /** A short second line, shown in the list only. */
    description?: React.ReactNode
    /** A trailing count or tag, shown in the list only. */
    badge?: React.ReactNode
    /** Nesting depth (a department under its agency): indents the row in the list. */
    depth?: 0 | 1 | 2
  }
>(({ className, children, icon, iconTone, iconTile, swatch, description, badge, depth = 0, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(overlay.option, overlay.optionInset, overlay.optionChecked, depth ? overlay.optionDepth[depth] : undefined, description ? "py-1.5" : undefined, className)}
    data-value={props.value}
    data-depth={depth || undefined}
    {...props}
  >
    <span className={overlay.optionIndicator}>
      <SelectPrimitive.ItemIndicator>
        <Check className="icon" aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <span className="flex min-w-0 flex-1 flex-col">
      <SelectPrimitive.ItemText>
        <span className="flex min-w-0 items-center gap-2">
          {swatch ? <span data-option-swatch={swatch} aria-hidden="true" className={cn(overlay.optionSwatch, OPTION_TONES[swatch].dot)} /> : null}
          {icon ? (
            iconTile ? (
              <span
                data-option-icon={iconTone ?? ""}
                data-option-tile=""
                aria-hidden="true"
                className={cn(overlay.optionIconTile, iconTone ? OPTION_TONES[iconTone].tile : overlay.optionIconTileNeutral)}
              >
                {icon}
              </span>
            ) : (
              <span
                data-option-icon={iconTone ?? ""}
                aria-hidden="true"
                className={iconTone ? cn("icon shrink-0", OPTION_TONES[iconTone].text) : overlay.optionIcon}
              >
                {icon}
              </span>
            )
          ) : null}
          <span className="truncate">{children}</span>
        </span>
      </SelectPrimitive.ItemText>
      {description ? <span className={overlay.optionDescription}>{description}</span> : null}
    </span>
    {badge !== undefined && badge !== null && badge !== false ? <span data-option-badge="" className={overlay.optionBadge}>{badge}</span> : null}
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn(overlay.separator, className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
